/**
 * NotificationContext — React context for managing the notification feed,
 * unread count, and polling. Uses the dedicated notification API client.
 *
 * Requirements: 4.2, 4.7, 4.8
 */

import React, { createContext, useCallback, useContext, useEffect, useReducer, useRef } from 'react';
import {
  getNotifications,
  getUnreadCount,
  markRead as apiMarkRead,
  markAllRead as apiMarkAllRead,
  NotificationItem,
} from '../api/notifications';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

interface NotificationState {
  readonly notifications: NotificationItem[];
  readonly unreadCount: number;
  readonly loading: boolean;
  readonly error: string | null;
}

const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
  loading: false,
  error: null,
};

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

type NotificationAction =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; payload: NotificationItem[] }
  | { type: 'FETCH_ERROR'; payload: string }
  | { type: 'SET_UNREAD_COUNT'; payload: number }
  | { type: 'MARK_READ_OPTIMISTIC'; payload: string }
  | { type: 'MARK_READ_REVERT'; payload: string }
  | { type: 'MARK_ALL_READ_OPTIMISTIC' }
  | { type: 'MARK_ALL_READ_REVERT'; payload: NotificationItem[] };

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

function computeUnread(notifications: NotificationItem[]): number {
  return notifications.filter((n) => !n.read).length;
}

function notificationReducer(state: NotificationState, action: NotificationAction): NotificationState {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, loading: true, error: null };

    case 'FETCH_SUCCESS':
      return {
        ...state,
        loading: false,
        notifications: action.payload,
        unreadCount: computeUnread(action.payload),
        error: null,
      };

    case 'FETCH_ERROR':
      return { ...state, loading: false, error: action.payload };

    case 'SET_UNREAD_COUNT':
      return { ...state, unreadCount: action.payload };

    case 'MARK_READ_OPTIMISTIC': {
      const updated = state.notifications.map((n) =>
        n.notificationId === action.payload ? { ...n, read: true } : n,
      );
      return { ...state, notifications: updated, unreadCount: computeUnread(updated) };
    }

    case 'MARK_READ_REVERT': {
      const reverted = state.notifications.map((n) =>
        n.notificationId === action.payload ? { ...n, read: false } : n,
      );
      return { ...state, notifications: reverted, unreadCount: computeUnread(reverted) };
    }

    case 'MARK_ALL_READ_OPTIMISTIC': {
      const allRead = state.notifications.map((n) => ({ ...n, read: true }));
      return { ...state, notifications: allRead, unreadCount: 0 };
    }

    case 'MARK_ALL_READ_REVERT':
      return {
        ...state,
        notifications: action.payload,
        unreadCount: computeUnread(action.payload),
      };

    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

interface NotificationContextValue {
  readonly notifications: NotificationItem[];
  readonly unreadCount: number;
  readonly loading: boolean;
  readonly error: string | null;
  fetchNotifications: () => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  markRead: (notificationId: string) => Promise<void>;
  markAllRead: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const POLL_INTERVAL_MS = 60_000;

// ---------------------------------------------------------------------------
// Provider component
// ---------------------------------------------------------------------------

interface NotificationProviderProps {
  readonly children: React.ReactNode;
  /** Override poll interval for testing (ms). */
  readonly pollInterval?: number;
}

export function NotificationProvider({
  children,
  pollInterval = POLL_INTERVAL_MS,
}: NotificationProviderProps): React.ReactElement {
  const [state, dispatch] = useReducer(notificationReducer, initialState);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchNotifications = useCallback(async (): Promise<void> => {
    dispatch({ type: 'FETCH_START' });
    try {
      const response = await getNotifications();
      dispatch({ type: 'FETCH_SUCCESS', payload: response.data });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to load notifications';
      dispatch({ type: 'FETCH_ERROR', payload: message });
    }
  }, []);

  const fetchUnreadCount = useCallback(async (): Promise<void> => {
    try {
      const count = await getUnreadCount();
      dispatch({ type: 'SET_UNREAD_COUNT', payload: count });
    } catch {
      // Silently fail — unread count polling is non-critical
    }
  }, []);

  const markRead = useCallback(async (notificationId: string): Promise<void> => {
    dispatch({ type: 'MARK_READ_OPTIMISTIC', payload: notificationId });
    try {
      await apiMarkRead(notificationId);
    } catch {
      dispatch({ type: 'MARK_READ_REVERT', payload: notificationId });
    }
  }, []);

  const markAllRead = useCallback(async (): Promise<void> => {
    const previousNotifications = state.notifications;
    dispatch({ type: 'MARK_ALL_READ_OPTIMISTIC' });
    try {
      await apiMarkAllRead();
    } catch {
      dispatch({ type: 'MARK_ALL_READ_REVERT', payload: previousNotifications });
    }
  }, [state.notifications]);

  // Poll unread count on interval when tab is visible
  useEffect(() => {
    function startPolling(): void {
      if (pollTimerRef.current) return;
      pollTimerRef.current = setInterval(() => {
        if (document.visibilityState === 'visible') {
          fetchUnreadCount();
        }
      }, pollInterval);
    }

    function stopPolling(): void {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    }

    function handleVisibilityChange(): void {
      if (document.visibilityState === 'visible') {
        fetchUnreadCount();
        startPolling();
      } else {
        stopPolling();
      }
    }

    // Initial fetch of unread count
    fetchUnreadCount();
    startPolling();

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchUnreadCount, pollInterval]);

  const value: NotificationContextValue = {
    notifications: state.notifications,
    unreadCount: state.unreadCount,
    loading: state.loading,
    error: state.error,
    fetchNotifications,
    fetchUnreadCount,
    markRead,
    markAllRead,
  };

  return (
    <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Access the notification context. Must be used within a NotificationProvider.
 */
export function useNotificationContext(): NotificationContextValue {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotificationContext must be used within a NotificationProvider');
  }
  return context;
}

// Export for testing
export { notificationReducer, initialState };
export type { NotificationState, NotificationAction, NotificationContextValue };
